import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle, BookOpen, Brain, CheckCircle, ChevronDown,
  ChevronRight, Clock, ClockIcon, FileText,
  Headphones, HelpCircle, Lightbulb, Play, Quote,
  Sparkles, X, Zap
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAdaptivePath } from '../../hooks/useAdaptivePath';
import { usePreview } from '../../hooks/usePreview';
import { useVoiceNarrator } from '../../hooks/useVoiceNarrator';
import { usePesaParcours } from '../../stores/usePesaParcours';
import { FloatingVideoPlayer } from './FloatingVideoPlayer';
import ImageComparator from './ImageComparator';
import QuizEngine from './QuizEngine';
import VocalOrTextInput from './VocalOrTextInput';

// DESIGN TOKENS
const S = {
  card:         'bg-slate-900/50 border border-white/8 rounded-[2rem] p-10 shadow-2xl relative overflow-hidden',
  cardAlt:      'bg-slate-900/40 border border-white/5 rounded-[2rem] p-8 relative overflow-hidden',
  text:         'text-slate-300',
  textMuted:    'text-slate-500',
  textWhite:    'text-white',
  accent:       'text-cyan-400',
  border:       'border border-white/5',
  borderAccent: 'border border-cyan-500/20',
  btn:          'px-10 py-4 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-black uppercase tracking-widest text-[10px] transition-all active:scale-95 shadow-lg shadow-cyan-900/20',
  btnSecondary: 'px-10 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl font-black uppercase tracking-widest text-[10px] transition-all',
  label:        'text-[10px] font-black uppercase tracking-[0.3em] font-mono',
};

const BLOOM_CONFIG = {
  'memorisation':  { color: 'text-slate-400', bg: 'bg-slate-500/10 border-slate-500/20' },
  'comprehension': { color: 'text-blue-400',  bg: 'bg-blue-500/10 border-blue-500/20' },
  'application':   { color: 'text-cyan-400',  bg: 'bg-cyan-500/10 border-cyan-500/20' },
  'analyse':       { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  'evaluation':    { color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
  'creation':      { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
};

const HARVEY_CONFIG = {
  'savoir':       { color: 'text-slate-400', label: 'SAVOIR' },
  'savoir-faire': { color: 'text-cyan-400',  label: 'SAVOIR-FAIRE' },
  'savoir-etre':  { color: 'text-purple-400', label: 'SAVOIR-ETRE' },
};

// VIDEO INTRO SCREEN
const VideoIntroScreen = ({ videoLink, videoTitle, videoDescription, moduleTitle, onEnter }) => {
  const [entered, setEntered] = useState(false);

  const normalizeYouTubeUrl = (url) => {
    if (!url) return url;
    if (url.includes('youtube.com/embed/')) return url;
    const watchMatch = url.match(/[?&]v=([^&#&]+)/);
    const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
    const id = (watchMatch || shortMatch)?.[1];
    if (id) return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
    return url;
  };

  const embedUrl = normalizeYouTubeUrl(videoLink);

  const handleEnter = () => {
    setEntered(true);
    setTimeout(() => onEnter(), 600);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: entered ? 0 : 1 }}
      transition={{ duration: 0.6 }}
      className="w-full h-full bg-slate-950 flex flex-col overflow-y-auto"
    >
      {/* Header bar — compact */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-white/5"
      >
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
          <span className="text-[9px] font-black font-mono uppercase tracking-[0.3em] text-cyan-500/60">
            Initialisation_Module
          </span>
        </div>
        <span className="text-[9px] font-mono text-white/15 uppercase tracking-widest hidden sm:block">
          Sequence_00 · Introduction
        </span>
      </motion.div>

      {/* Main content — colonne par défaut, ligne seulement si vraiment large */}
      <div className="flex-1 flex flex-col min-[900px]:flex-row w-full px-5 py-6 gap-6 min-[900px]:gap-0 min-[900px]:py-10 min-[900px]:px-8">

        {/* Left — texte + CTA */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex flex-col justify-center min-[900px]:w-[38%] min-[900px]:pr-10 shrink-0"
        >
          <span className="inline-block px-2.5 py-1 rounded-full border border-cyan-500/20 bg-cyan-500/5 text-[9px] font-black font-mono uppercase tracking-[0.25em] text-cyan-500/70 mb-4 w-fit">
            Video_Introductive
          </span>

          <h1 className="text-xl min-[900px]:text-2xl font-black text-white tracking-tighter leading-tight mb-3 uppercase">
            {videoTitle || moduleTitle || 'Introduction'}
          </h1>

          {videoDescription && (
            <p className="text-xs text-slate-400 leading-relaxed mb-4 max-w-xs">
              {videoDescription}
            </p>
          )}

          <div className="w-10 h-px bg-gradient-to-r from-cyan-500/50 to-transparent mb-5" />

          <div className="flex items-center gap-2.5 mb-6 p-2.5 rounded-xl border border-white/5 bg-white/[0.02] w-fit">
            <div className="w-6 h-6 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center shrink-0">
              <Play size={9} className="text-cyan-400 ml-0.5" fill="currentColor" />
            </div>
            <div>
              <p className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Avant de commencer</p>
              <p className="text-[10px] text-slate-400">Visionnez l'introduction pour contextualiser</p>
            </div>
          </div>

          <motion.button
            onClick={handleEnter}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className="group relative inline-flex items-center gap-3 px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-black uppercase tracking-widest text-[10px] transition-all shadow-lg shadow-cyan-900/30 self-start overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <span className="relative z-10">Lancer le module</span>
            <ChevronRight size={11} className="relative z-10 group-hover:translate-x-1 transition-transform" />
          </motion.button>

          <p className="mt-3 text-[9px] font-mono text-white/15 uppercase tracking-widest">
            Video accessible pendant toute la sequence
          </p>
        </motion.div>

        {/* Right — video player */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex-1 flex flex-col min-w-0"
        >
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-cyan-950/50 bg-black">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-cyan-500/10 via-transparent to-transparent pointer-events-none z-10" />
            <div className="relative aspect-video w-full bg-slate-950">
              <iframe
                className="w-full h-full border-none"
                src={embedUrl}
                title={videoTitle || 'Video introductive'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="px-4 py-2 border-t border-white/5 bg-black/60 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500/60" />
              <span className="text-[9px] font-mono text-white/20 uppercase tracking-widest flex-1 truncate">
                {videoTitle || 'Introduction'}
              </span>
              <span className="text-[8px] font-mono text-white/10 uppercase shrink-0">HD</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-3 flex items-center gap-2 px-1"
          >
            <div className="flex gap-1">
              {[0,1,2].map(i => (
                <motion.div
                  key={i}
                  className="w-1 h-1 rounded-full bg-cyan-500/30"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
            </div>
            <span className="text-[9px] font-mono text-white/20 uppercase tracking-widest">
              Visionnage libre
            </span>
          </motion.div>
        </motion.div>
      </div>

      <div className="shrink-0 h-px w-full bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
    </motion.div>
  );
};

const ContentStepVideoHeader = ({ videoLink, videoTitle, videoDescription }) => {
  if (!videoLink) return null;
  const normalizeYouTubeUrl = (url) => {
    if (!url) return url;
    if (url.includes('youtube.com/embed/')) return url;
    const watchMatch = url.match(/[?&]v=([^&#&]+)/);
    const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
    const id = (watchMatch || shortMatch)?.[1];
    if (id) return `https://www.youtube.com/embed/${id}`;
    return url;
  };
  const embedUrl = normalizeYouTubeUrl(videoLink);
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-10 rounded-2xl overflow-hidden border border-white/8 bg-black/40 shadow-2xl"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
          <span className="text-[9px] font-black font-mono uppercase tracking-[0.25em] text-cyan-500/70">Video_Introductive</span>
        </div>
        {videoTitle && <span className="text-[10px] text-slate-500 truncate max-w-xs">{videoTitle}</span>}
      </div>
      <div className="relative aspect-video w-full bg-slate-950">
        <iframe className="w-full h-full border-none" src={embedUrl} title={videoTitle || 'Video'} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
      {videoDescription && (
        <div className="px-5 py-3 border-t border-white/5 bg-white/[0.01]">
          <p className="text-[11px] text-slate-500 leading-relaxed italic">{videoDescription}</p>
        </div>
      )}
    </motion.div>
  );
};

const StepMetaBar = ({ step }) => {
  const bloom = BLOOM_CONFIG[step?.bloomLevel];
  const harvey = HARVEY_CONFIG[step?.harveyTier];
  if (!bloom && !harvey && !step?.competencySignal) return null;
  return (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-center gap-2 mb-6 px-1">
      {bloom && (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[9px] font-black font-mono uppercase tracking-widest ${bloom.bg} ${bloom.color}`}>
          <Brain size={9} /> Bloom · {step.bloomLevel}
        </span>
      )}
      {harvey && (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/5 bg-white/[0.03] text-[9px] font-black font-mono uppercase tracking-widest ${harvey.color}`}>
          {harvey.label}
        </span>
      )}
      {step?.competencySignal && (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/5 bg-white/[0.03] text-[9px] font-black font-mono uppercase tracking-widest text-white/30">
          <Zap size={9} className="text-cyan-600" />
          {step.competencySignal.name} · {step.competencySignal.level}
        </span>
      )}
    </motion.div>
  );
};

const PesaUnlockBanner = ({ unlocksPesaContent }) => {
  const [open, setOpen] = useState(false);
  if (!unlocksPesaContent?.agentDialogue && !unlocksPesaContent?.simulationHint) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 rounded-xl border border-cyan-500/10 bg-cyan-500/[0.04] overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3 text-left">
        <div className="flex items-center gap-2">
          <Sparkles size={10} className="text-cyan-500/60" />
          <span className="text-[9px] font-black font-mono uppercase tracking-[0.25em] text-cyan-500/60">Deblockage_PESA</span>
        </div>
        <ChevronDown size={12} className={`text-cyan-500/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-5 pb-4 space-y-2.5 border-t border-cyan-500/10">
              {unlocksPesaContent.agentDialogue && (
                <p className="text-[11px] text-slate-400 leading-relaxed pt-3">
                  <span className="text-cyan-500/60 font-mono text-[9px] uppercase tracking-widest">Agent</span>
                  <br />{unlocksPesaContent.agentDialogue}
                </p>
              )}
              {unlocksPesaContent.simulationHint && (
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  <span className="text-white/20 font-mono text-[9px] uppercase tracking-widest">Sim</span>
                  <br />{unlocksPesaContent.simulationHint}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const PesaEchoPanel = ({ agent, context }) => {
  if (!agent && !context) return null;
  return (
    <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex gap-4 p-5 rounded-2xl border border-cyan-500/10 bg-cyan-500/[0.03]">
      <div className="shrink-0 mt-0.5">
        <div className="w-6 h-6 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
          <span className="text-[8px] font-black text-cyan-500">P</span>
        </div>
      </div>
      <div>
        {agent && <p className="text-[9px] font-black font-mono uppercase tracking-[0.25em] text-cyan-500/60 mb-1">PESA_ECHO · {agent}</p>}
        {context && <p className="text-[11px] text-slate-500 leading-relaxed italic">{context}</p>}
      </div>
    </motion.div>
  );
};

const KeyQuote = ({ quote }) => {
  if (!quote?.text) return null;
  return (
    <blockquote className="mt-4 pl-4 border-l-2 border-white/10">
      <p className="text-[11px] text-slate-400 italic leading-relaxed">« {quote.text} »</p>
      <footer className="mt-1.5 text-[9px] font-mono text-white/20 uppercase tracking-widest">
        — {quote.author}{quote.year ? `, ${quote.year}` : ''}{quote.source ? ` · ${quote.source}` : ''}
      </footer>
    </blockquote>
  );
};

const PesaTransferBadge = ({ actionId, text }) => {
  if (!actionId && !text) return null;
  return (
    <div className="mt-4 flex gap-2 items-start p-3 rounded-lg bg-white/[0.02] border border-white/5">
      <ChevronRight size={10} className="text-cyan-600 shrink-0 mt-0.5" />
      <div>
        {actionId && <span className="text-[8px] font-mono text-cyan-600/60 uppercase tracking-widest block mb-0.5"> {actionId}</span>}
        {text && <p className="text-[11px] text-slate-500 leading-relaxed">{text}</p>}
      </div>
    </div>
  );
};

const ContextualNote = ({ type, content }) => {
  if (!content) return null;
  const configs = {
    realityCheck:           { label: 'Realite_terrain', color: 'text-amber-500/60', bg: 'bg-amber-500/5 border-amber-500/10' },
    paradoxe:               { label: 'Paradoxe', color: 'text-purple-500/60', bg: 'bg-purple-500/5 border-purple-500/10' },
    differentiation:        { label: 'A_distinguer', color: 'text-blue-400/60', bg: 'bg-blue-500/5 border-blue-500/10' },
    critiqueBourdieusienne: { label: 'Critique_Bourdieu', color: 'text-orange-500/60', bg: 'bg-orange-500/5 border-orange-500/10' },
    conditionalSuccess:     { label: 'Condition_succes', color: 'text-emerald-500/60', bg: 'bg-emerald-500/5 border-emerald-500/10' },
  };
  const cfg = configs[type] || { label: type, color: 'text-white/30', bg: 'bg-white/[0.02] border-white/5' };
  return (
    <div className={`mt-3 p-3 rounded-lg border ${cfg.bg}`}>
      <p className={`text-[8px] font-black font-mono uppercase tracking-widest mb-1 ${cfg.color}`}>{cfg.label}</p>
      <p className="text-[11px] text-slate-500 leading-relaxed">{content}</p>
    </div>
  );
};

const EvaluationGrid = ({ grid }) => {
  const [open, setOpen] = useState(false);
  if (!grid?.length) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 rounded-xl border border-white/5 overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3 bg-white/[0.02] text-left hover:bg-white/[0.04] transition-colors">
        <div className="flex items-center gap-2">
          <BookOpen size={10} className="text-white/30" />
          <span className="text-[9px] font-black font-mono uppercase tracking-[0.25em] text-white/30">Grille_Evaluation</span>
        </div>
        <ChevronDown size={12} className={`text-white/20 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="divide-y divide-white/5">
              {grid.map((item, i) => (
                <div key={i} className="px-5 py-4">
                  <p className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-3">{item.criterion}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {['novice', 'competent', 'expert'].map((lvl) => (
                      <div key={lvl} className={`p-2.5 rounded-lg border ${lvl === 'expert' ? 'border-emerald-500/20 bg-emerald-500/5' : lvl === 'competent' ? 'border-cyan-500/10 bg-cyan-500/5' : 'border-white/5 bg-white/[0.02]'}`}>
                        <p className={`text-[8px] font-black font-mono uppercase tracking-widest mb-1 ${lvl === 'expert' ? 'text-emerald-500/60' : lvl === 'competent' ? 'text-cyan-500/60' : 'text-white/20'}`}>{lvl}</p>
                        <p className="text-[10px] text-slate-500 leading-relaxed">{item.levels[lvl]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const HarvestConsequence = ({ consequence }) => {
  if (!consequence) return null;
  return (
    <div className="mt-6 flex gap-3 p-4 rounded-xl border border-cyan-500/10 bg-cyan-500/[0.03]">
      <Sparkles size={12} className="text-cyan-500/40 shrink-0 mt-0.5" />
      <p className="text-[11px] text-slate-500 leading-relaxed italic">
        <span className="text-cyan-500/50 font-mono text-[8px] uppercase tracking-widest not-italic block mb-1">Impact_PESA</span>
        {consequence}
      </p>
    </div>
  );
};

const ClosureFooter = ({ step }) => {
  const hasAny = step?.retentionMechanism || step?.nextStepForeshadow || step?.pesaConnectionReveal;
  const [open, setOpen] = useState(false);
  if (!hasAny) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10 border-t border-white/5 pt-8">
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 text-[9px] font-mono text-white/20 hover:text-white/40 transition-colors uppercase tracking-widest">
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        Aller_plus_loin
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-6 space-y-4 overflow-hidden">
            {step.retentionMechanism && (
              <div className="flex gap-3 p-4 rounded-xl border border-purple-500/10 bg-purple-500/[0.03]">
                <Brain size={11} className="text-purple-500/40 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[8px] font-mono text-purple-500/40 uppercase tracking-widest mb-1">Ancrage_memoriel</p>
                  <p className="text-[11px] text-slate-500 leading-relaxed">{step.retentionMechanism}</p>
                </div>
              </div>
            )}
            {step.nextStepForeshadow && (
              <div className="flex gap-3 p-4 rounded-xl border border-amber-500/10 bg-amber-500/[0.03]">
                <Lightbulb size={11} className="text-amber-500/40 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[8px] font-mono text-amber-500/40 uppercase tracking-widest mb-1">Ce_qui_vient</p>
                  <p className="text-[11px] text-slate-500 leading-relaxed">{step.nextStepForeshadow}</p>
                </div>
              </div>
            )}
            {step.pesaConnectionReveal && (
              <div className="flex gap-3 p-4 rounded-xl border border-cyan-500/10 bg-cyan-500/[0.03]">
                <Sparkles size={11} className="text-cyan-500/40 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[8px] font-mono text-cyan-500/40 uppercase tracking-widest mb-1">Connexion_PESA</p>
                  <p className="text-[11px] text-slate-500 leading-relaxed">{step.pesaConnectionReveal}</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const NarrativeArcBanner = ({ narrativeArc }) => {
  if (!narrativeArc?.epistemicShift) return null;
  const positionColors = { exposition: 'text-blue-400/60', developpement: 'text-cyan-400/60', resolution: 'text-emerald-400/60' };
  const color = positionColors[narrativeArc.position] || 'text-white/20';
  return (
    <div className="mb-10 px-6 py-4 rounded-2xl border border-white/5 bg-white/[0.01]">
      <div className="flex items-center gap-3 mb-2">
        <span className={`text-[8px] font-black font-mono uppercase tracking-[0.3em] ${color}`}>Arc_{narrativeArc.position}</span>
        {narrativeArc.tensionLevel && (
          <div className="flex gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`w-3 h-1 rounded-full transition-all ${i < narrativeArc.tensionLevel ? 'bg-cyan-500/40' : 'bg-white/5'}`} />
            ))}
          </div>
        )}
      </div>
      <p className="text-[12px] text-slate-400 leading-relaxed italic">{narrativeArc.epistemicShift}</p>
      {narrativeArc.transferBridge && (
        <p className="mt-2 text-[10px] text-slate-600 leading-relaxed">
          <span className="text-cyan-600/40 font-mono text-[8px] uppercase tracking-widest not-italic">PESA  </span>
          {narrativeArc.transferBridge}
        </p>
      )}
    </div>
  );
};

const EnrichedModal = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;
  const renderSection = (section, idx) => {
    switch (section.type) {
      case 'text': return (
        <div key={idx} className="prose prose-invert prose-slate max-w-none">
          <p className="text-base leading-relaxed text-slate-300 whitespace-pre-line">{section.content}</p>
        </div>
      );
      case 'key-quote': return (
        <div key={idx} className="relative p-6 rounded-xl bg-cyan-500/5 border border-cyan-500/20 backdrop-blur-sm">
          <Quote size={20} className="absolute top-4 left-4 text-cyan-500/40" />
          <blockquote className="pl-8 text-lg italic text-white/90 leading-relaxed">"{section.quote}"</blockquote>
          {section.author && <footer className="mt-4 text-right text-sm text-cyan-400 font-mono">— {section.author}</footer>}
        </div>
      );
      case 'video': return (
        <div key={idx} className="space-y-3">
          <div className="aspect-video rounded-xl overflow-hidden border border-white/10 bg-black/60 shadow-lg">
            <iframe className="w-full h-full" src={section.url} title={section.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
          <div className="flex justify-between items-center px-2">
            <span className="text-xs text-slate-400">{section.caption}</span>
            <span className="text-[10px] font-mono text-slate-600 uppercase tracking-wider">{section.source || 'source video'}</span>
          </div>
        </div>
      );
      case 'audio-podcast': return (
        <div key={idx} className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 flex items-center gap-4 hover:bg-emerald-500/10 transition-colors">
          <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 shrink-0">
            <Headphones size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-white truncate">{section.title}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] font-mono text-emerald-400/70 uppercase tracking-wider">Podcast</span>
              {section.transcriptAvailable && <><span className="w-1 h-1 rounded-full bg-emerald-500/60" /><span className="text-[10px] font-mono text-emerald-400/70 uppercase tracking-wider">transcription</span></>}
            </div>
          </div>
          <button className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-lg uppercase tracking-wider transition-all shadow-md active:scale-95">Ecouter</button>
        </div>
      );
      case 'pesa-link': return (
        <div key={idx} className="relative p-5 rounded-xl border border-dashed border-cyan-500/30 bg-cyan-500/5">
          <div className="absolute -top-3 left-4 px-3 py-0.5 bg-slate-950 text-[9px] font-mono text-cyan-400 uppercase tracking-wider border border-cyan-500/30 rounded-full">SIMULATION PESA</div>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed italic">{section.content}</p>
        </div>
      );
      default: return null;
    }
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} className="bg-slate-950/90 backdrop-blur-xl border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/50" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/30 rounded text-[10px] font-mono text-cyan-400 uppercase tracking-wider">approfondissement</span>
                  {data.conceptualDepth && <span className="px-2 py-0.5 bg-slate-800 border border-white/10 rounded text-[9px] font-mono text-slate-400">NIVEAU 0{data.conceptualDepth}</span>}
                </div>
                <h2 className="text-2xl font-bold text-white tracking-tight">{data.title || 'Concept approfondi'}</h2>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-8 no-scrollbar">
              {data.contentSections?.map((section, idx) => renderSection(section, idx))}
            </div>
            <div className="p-5 border-t border-white/5 bg-white/[0.02]">
              <button onClick={onClose} className="w-full py-3.5 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white font-bold text-sm rounded-xl uppercase tracking-wider transition-all shadow-lg shadow-cyan-900/30 active:scale-[0.98]">
                {data.closeAction || 'Continuer'}
              </button>
              {data.nextCardUnlock && <p className="text-center text-[9px] text-slate-600 mt-3 font-mono uppercase tracking-wider">Deverrouillage de la carte suivante active</p>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const getTeaserIcon = (visual) => {
  if (!visual) return null;
  if (visual.match(/^(\p{Emoji}|<svg)/u)) return visual;
  const name = visual.replace(/^icone:\s*/i, '').trim().toLowerCase();
  const iconMap = { 'silhouettes diverses reunies sous un meme toit': '\u{1F465}', 'texte de loi avec un coeur': '\u{1F4DC}\u{2764}\u{FE0F}', 'document avec deux signatures': '\u{1F4DD}\u{270D}\u{FE0F}' };
  return iconMap[name] || '\u{1F4CC}';
};

// SCENARIO PLAYER
const ScenarioPlayer = ({ data, onHarvest, onComplete, moduleData }) => {
  const {
    ui, modules, setVideoScreenFocus, currentModule, focusResource, toggleShowNarrator,
    closeActiveItem, activeItem, validateEvidence, validateGranule,
    resolveAsset, setCurrentStep, embeddedMedia, setEmbeddedMedia,
    clearEmbeddedMedia, validateComprehensionQuiz, validateDiagnosticQuiz,
  } = usePesaParcours();
  const showNarrator = ui.showNarrator;
  const { isPreview } = usePreview();
  const { analyserRef, isPlaying, isLoading, currentTrigger, narrate, stopNarration } = useVoiceNarrator({ resolveAsset });

  const [startTime] = useState(Date.now());
  const content = data?.content || data || {};
  const [activeModal, setActiveModal] = useState(null);
  const [showBonusModal, setShowBonusModal] = useState(false);

  const isCapstone = activeItem?.type === 'capstone' || activeItem?.isCapstone === true || content?.centralWorkspace?.type === 'capstone-end';

  // PATCH: getInitialPhase insere video-intro en premier si videoLink present
  const getInitialPhase = (c) => {
    if (c.videoLink) return 'video-intro';
    if (c.preActivityReflection) return 'pre-reflection';
    if (c.diagnosticQuiz?.questions?.length > 0) return 'diagnostic-quiz';
    if (c.steps?.length > 0) return 'steps';
    return 'complete';
  };

  const getPostVideoPhase = (c) => {
    if (c.preActivityReflection) return 'pre-reflection';
    if (c.diagnosticQuiz?.questions?.length > 0) return 'diagnostic-quiz';
    if (c.steps?.length > 0) return 'steps';
    return 'complete';
  };

  const [phase, setPhase] = useState(() => getInitialPhase(content));
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [harvestedData, setHarvestedData] = useState({});
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [diagnosticScore, setDiagnosticScore] = useState(60);
  const [selfAssessmentValue, setSelfAssessmentValue] = useState(null);
  const [showSelfGapFeedback, setShowSelfGapFeedback] = useState(false);
  const [selfGapFeedback, setSelfGapFeedback] = useState({ type: '', message: '' });

  const prevScenarioId = useRef(null);
  const prevPhaseRef = useRef(phase);
  const hasCalledComplete = useRef(false);
  const pendingTimers = useRef([]);
  const containerRef = useRef(null);

  const safeTimeout = (fn, ms) => {
    const id = setTimeout(() => { pendingTimers.current = pendingTimers.current.filter(t => t !== id); fn(); }, ms);
    pendingTimers.current.push(id);
    return id;
  };

  useEffect(() => () => { pendingTimers.current.forEach(clearTimeout); }, []);
  useEffect(() => {
    setVideoScreenFocus(true);
    return () => setVideoScreenFocus(false);
  }, []);


  const {
    steps = [], preActivityReflection, diagnosticQuiz, comprehensionQuiz,
    mediatorOnFail, alternativePath, narrativeArc, pesaEchoId, unlocksPesaContent,
    videoLink, videoTitle, videoDescription,
  } = content;

  const { adaptedSteps, cardFilter, learnerProfile, profileBadge } = useAdaptivePath({ steps, harvestedData, diagnosticScore });
  const currentStep = adaptedSteps[currentStepIdx];

  useEffect(() => {
    const scenarioId = data?.id || data?._id || JSON.stringify(data?.steps?.map(s => s.stepId));
    if (scenarioId !== prevScenarioId.current) {
      prevScenarioId.current = scenarioId;
      hasCalledComplete.current = false;
      pendingTimers.current.forEach(clearTimeout);
      pendingTimers.current = [];
      const initial = getInitialPhase(data?.content || data || {});
      prevPhaseRef.current = initial;
      setPhase(initial);
      setCurrentStepIdx(0);
      setHarvestedData({});
      setSelectedEmotions([]);
      setIsTransitioning(false);
      setActiveModal(null);
    }
  }, [data]);

  useEffect(() => { setSelectedEmotions([]); }, [currentStepIdx]);
  useEffect(() => { setCurrentStep(steps[currentStepIdx]); }, [currentStepIdx, steps]);
  useEffect(() => {
    if (phase === 'complete' && prevPhaseRef.current !== 'complete' && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      safeTimeout(() => { onComplete?.(); }, 2500);
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  const updateHarvest = (key, value) => {
    if (!key) { console.warn('[HARVEST] cle manquante !'); return; }
    const newData = { ...harvestedData, [key]: value };
    setHarvestedData(newData);
    if (isPreview) { console.log(`%c[PREVIEW_MODE] Harvest simule (${key}):`, 'color: #00e5ff; font-weight: bold', value); return; }
    onHarvest?.(newData);
  };

  const handleVocalSubmit = (key) => async ({ text, mediaBase64, mediaType, dureeEnreg }) => {
    updateHarvest(key, { type: mediaType || 'text', text: text || null, mediaBase64: mediaBase64 || null, mediaType, duree: dureeEnreg || 0, ts: Date.now() });
  };

  const handleCompleteProtocol = async () => {
    if (isPreview) { setPhase('complete'); return; }
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    const expectedDuration = (data.duration || content.duration || 10) * 60;
    try {
      await validateEvidence(data.id || data._id, { type: 'theory', timeSpent, expectedDuration, harvestKeys: Object.keys(harvestedData) });
    } catch (err) { console.error('validateEvidence:', err); } finally { setPhase('complete'); }
  };

  const handleQuizSuccess = async (quizResults) => {
    if (phase === 'diagnostic-quiz') { setPhase(adaptedSteps.length > 0 ? 'steps' : 'complete'); return; }
    if (phase === 'quiz') {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      const expectedDuration = (data.duration || content.duration || 10) * 60;
      if (!isPreview) {
        try {
          await validateEvidence(data.id || data._id, { type: 'quiz', timeSpent, expectedDuration, granuleId: `comprehension-${data.id || data._id}`, score: quizResults?.score || 100, passed: true, answers: quizResults?.answers || [] });
        } catch (err) { console.error('validateEvidence quiz:', err); }
      }
      setPhase('complete'); return;
    }
    handleCompleteProtocol();
  };

  const handleQuizFail = () => {
    if (mediatorOnFail) { toggleShowNarrator(); setPhase('mediator'); }
    else if (alternativePath) setPhase('alternative');
    else handleQuizSuccess();
  };

  const handleNavigation = async (targetPhase, shouldValidate = false, quizResults = null) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    if (shouldValidate && !isPreview) {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      const expectedDuration = (data.duration || content.duration || 10) * 60;
      try {
        await validateEvidence(data.id || data._id, { type: quizResults ? 'quiz' : 'theory', timeSpent, expectedDuration, score: quizResults?.score || 100, passed: true });
      } catch (err) { console.error('Erreur validation:', err); }
    }
    safeTimeout(() => { setPhase(targetPhase); safeTimeout(() => setIsTransitioning(false), 300); }, 2500);
  };

  const resolveHarvestEcho = (echo, harvestedData) => {
    const raw = harvestedData[echo.sourceKey];
    const value = typeof raw === 'string' ? raw : raw?.text ?? raw;
    return (echo.injectionTemplate || '{value}').replace('{value}', value ?? echo.fallback);
  };

  const resolveClosureText = (dyn, harvestedData, learnerProfile) => {
    let text = dyn.base || '';
    if (dyn.harvestEchoes) {
      for (const echo of dyn.harvestEchoes) {
        let value = harvestedData[echo.key];
        if (echo.emotionExtractor === 'dominant' && Array.isArray(value?.selected)) {
          const counts = value.selected.reduce((acc, emo) => { acc[emo] = (acc[emo] || 0) + 1; return acc; }, {});
          const dominant = Object.entries(counts).sort((a,b) => b[1]-a[1])[0]?.[0];
          value = echo.qualityLevels?.[dominant] || dominant || echo.fallback || '';
        } else { value = typeof value === 'string' ? value : value?.text ?? value; }
        text = text.replace(`{${echo.key}}`, value ?? echo.fallback ?? '');
      }
    }
    if (dyn.closing) text += dyn.closing;
    return text;
  };

  const handleSubmitWithSelfAssessment = async (stepId, value) => {
    await handleVocalSubmit(stepId)(value);
    if (currentStep.selfAssessment && selfAssessmentValue !== null && currentStep.evaluationGrid?.mentorLevel) {
      const feedbackKey = selfAssessmentValue > currentStep.evaluationGrid.mentorLevel ? 'overestimated' : selfAssessmentValue < currentStep.evaluationGrid.mentorLevel ? 'underestimated' : 'aligned';
      const feedback = currentStep.selfAssessment.feedbackOnGap?.[feedbackKey];
      setSelfGapFeedback({ type: feedbackKey, message: feedback || '' });
      setShowSelfGapFeedback(true);
    }
  };

  useEffect(() => {
    if (currentStep?.type === 'personal-synthesis' && currentStep.selfAssessment) {
      setSelfAssessmentValue(harvestedData[currentStep.selfAssessment.harvestKey] ?? null);
      setShowSelfGapFeedback(false);
      setSelfGapFeedback({ type: '', message: '' });
    }
  }, [currentStep?.type, currentStep?.selfAssessment, harvestedData]);
  
  useEffect(() => {
    if (phase === 'steps' && containerRef.current) {
      const timeoutId = setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
      }, 100); // small delay for animation
      return () => clearTimeout(timeoutId);
    }
  }, [currentStepIdx, phase]);
 

  // PHASE: VIDEO-INTRO
  if (phase === 'video-intro') {
    return (
      <VideoIntroScreen
        videoLink={videoLink}
        videoTitle={videoTitle}
        videoDescription={videoDescription}
        moduleTitle={data?.title || content?.title}
        onEnter={() => setPhase(getPostVideoPhase(content))}
      />
    );
  }

  // PHASE: PRE-REFLECTION
  if (phase === 'pre-reflection' && preActivityReflection) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={S.card}>
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
          <div className="flex items-center gap-2 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
            <span className={`${S.label} text-cyan-500/70`}>Briefing_Initial</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-6 tracking-tight">{preActivityReflection.text.split('\n')[0]}</h2>
          <div className="text-slate-400 text-sm space-y-3 mb-10 leading-relaxed pl-6 border-l-2 border-white/5">
            {preActivityReflection.text.split('\n').slice(1).filter(l => l.trim()).map((l, i) => <p key={i}>{l}</p>)}
          </div>
          <div className="space-y-8">
            {preActivityReflection.questions?.map((q, i) => (
              <div key={i}>
                <p className="text-cyan-400 font-bold mb-4 text-lg tracking-tight flex gap-3">
                  <span className="opacity-30 font-mono text-sm mt-1">0{i + 1}</span>{q}
                </p>
                <VocalOrTextInput stepId={`pre-q${i}`} label="Consigner ma reflexion" onSubmit={handleVocalSubmit(`pre-q${i}`)} />
              </div>
            ))}
          </div>
          <div className="text-right mt-12 pt-8 border-t border-white/5">
            <button onClick={() => setPhase('steps')} className={S.btn}>Initialiser la sequence</button>
          </div>
        </motion.div>
      </div>
    );
  }

  // PHASE: DIAGNOSTIC QUIZ
  if (phase === 'diagnostic-quiz' && diagnosticQuiz?.questions?.length > 0) {
    const normalizedQuestions = diagnosticQuiz.questions.map((q, idx) => {
      const opts = Array.isArray(q.options)
        ? q.options.map((opt, oIdx) => ({ text: typeof opt === 'string' ? opt : opt.text || '', isCorrect: opt.isCorrect || q.correctAnswer === oIdx || false }))
        : [];
      return { ...q, _id: q._id || `diag-q-${idx}`, options: opts };
    });
    const normalizedQuiz = { title: diagnosticQuiz.title || 'Diagnostic initial', passingScore: diagnosticQuiz.passingScore || 60, questions: normalizedQuestions };

    const handleDiagnosticComplete = (quizResults) => {
      const moduleId = currentModule?.id || currentModule?._id;
      if (moduleId) validateDiagnosticQuiz(moduleId, { score: quizResults.score || 0, passed: quizResults.passed || false, quizId: diagnosticQuiz._id || `diagnostic-${moduleId}`, answers: quizResults.answers || [] });
      setDiagnosticScore(quizResults.score || 0);
      safeTimeout(() => { setPhase(adaptedSteps.length > 0 ? 'steps' : 'complete'); validateGranule(data.id || data._id, { passed: true }); }, 2500);
    };

    const handleDiagnosticFail = (quizResults) => {
      const moduleId = currentModule?.id || currentModule?._id;
      if (moduleId) validateDiagnosticQuiz(moduleId, { score: quizResults.score || 0, passed: false, quizId: diagnosticQuiz._id || `diagnostic-${moduleId}`, answers: quizResults.answers || [] });
      safeTimeout(() => { handleQuizFail(); validateGranule(data.id || data._id, { passed: false }); }, 2500);
    };

    // Le parent LearningArea est overflow-y-auto — on ne peut pas utiliser height:100%.
    // Solution : header + intro sticky via position:sticky, QuizEngine prend toute la hauteur
    // naturelle. Le scroll se fait dans le LearningArea parent, comme pour les phases 'steps'.
    return (
      <div className="h-full w-full flex flex-col overflow-hidden">
        {/* Header sticky — colle au haut du scroll parent */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-white/5 bg-emerald-500/10 backdrop-blur-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]" />
            <div>
              <span className={`${S.label} text-emerald-400 block`}>Diagnostic_Initial</span>
              <h2 className="text-sm font-bold uppercase text-white">{normalizedQuiz.title}</h2>
            </div>
          </div>
          <div className="text-right">
            <span className={`${S.label} text-white/30`}>Score_minimum</span>
            <p className="font-black font-mono text-emerald-400">{normalizedQuiz.passingScore}%</p>
          </div>
        </div>
        {/* Introduction */}
        <div className="px-6 py-3 border-b border-white/5">
          <p className="text-sm text-center text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Ce diagnostic adapte le parcours a votre niveau actuel. Il n'y a pas de mauvaise reponse — seulement une cartographie precise de vos forces.
          </p>
        </div>
        {/* QuizEngine — hauteur naturelle, scroll géré par LearningArea */}
        <QuizEngine
          quizData={normalizedQuiz}
          mode="diagnostic"
          onComplete={handleDiagnosticComplete}
          onFail={handleDiagnosticFail}
          mediatorOnFail={mediatorOnFail}
          onClose={closeActiveItem}
          embedded={true}
          learnerProfile={learnerProfile}
        />
      </div>
    );
  }

  // PHASE: STEPS
  if (phase === 'steps') {
    const stepId = currentStep?.stepId || `step-${currentStepIdx}`;
    return (
      <div className={isCapstone ? 'min-h-screen bg-gradient-to-b from-slate-950 via-black to-slate-950 py-20' : 'min-h-screen'} ref={containerRef}>
        {isPreview && (
          <div className="fixed top-0 left-0 w-full z-[100] bg-emerald-500/10 border-b border-emerald-500/30 backdrop-blur-sm py-1 flex justify-center items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[9px] font-black text-emerald-500 uppercase font-mono tracking-[0.2em]">Mode_Simulation_Actif // Donnees_Non_Persistees</span>
          </div>
        )}
        <div className={`max-w-6xl mx-auto ${isCapstone ? 'px-8 lg:px-20' : 'px-6'}`} >
          {isCapstone && (
            <div className="flex justify-center gap-2 mb-16">
              {steps.map((_, i) => (
                <div key={i} className={`h-2 w-16 rounded-full transition-all duration-700 ${i <= currentStepIdx ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 shadow-[0_0_15px_rgba(34,211,238,0.6)]' : 'bg-white/10'}`} />
              ))}
            </div>
          )}
          <AnimatePresence mode="wait">
            <motion.div key={currentStepIdx} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
              {/* Arc narratif */}
              {currentStepIdx === 0 && narrativeArc && <NarrativeArcBanner narrativeArc={narrativeArc} />}
              {/* Deblockage PESA */}
              {currentStepIdx === 0 && unlocksPesaContent && <PesaUnlockBanner unlocksPesaContent={unlocksPesaContent} />}

              {/* Header step */}
              <header className="mb-10 border-l-4 border-cyan-500 pl-8">
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-cyan-400 text-[10px] font-mono font-black uppercase tracking-[0.2em]">{currentStep?.type || 'module'}</span>
                  <div className="h-px w-8 bg-white/10" />
                  <span className="text-white/20 text-[10px] font-mono">Etape_{currentStepIdx + 1} / {adaptedSteps.length}</span>
                </div>
                <h1 className="text-3xl font-bold text-white tracking-tighter uppercase mb-3">{currentStep?.title || 'Analyse'}</h1>
                <StepMetaBar step={currentStep} />
              </header>

              {learnerProfile.type !== 'standard' && currentStepIdx === 0 && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/20 bg-cyan-500/5">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                  <span className="text-[9px] font-mono text-cyan-500/70 uppercase tracking-widest">Parcours_Adaptatif // {profileBadge}</span>
                  {learnerProfile.mode === 'renforcement' && <span className="ml-2 text-[8px] font-mono text-white/20 uppercase">[Steps introductifs masques]</span>}
                </motion.div>
              )}

              {/* MICRO-QUIZ */}
              {currentStep?.type === 'micro-quiz' && currentStep.questions && (
                <div className={`my-10 ${S.cardAlt}`}>
                  <div className="absolute top-0 right-0 p-4 opacity-10 font-mono text-[40px] font-black select-none">QUIZ</div>
                  <h3 className="text-lg font-bold text-white mb-8 flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full shadow-[0_0_8px_#a855f7]" />
                    {currentStep.title || 'Verification de donnees'}
                  </h3>
                  <QuizEngine
                    quizData={{ questions: currentStep.questions.map((q, idx) => ({ ...q, _id: q._id || `micro-${idx}`, feedback: q.feedback || (q.errorAnalysis?.[0]?.remediation ? `${q.feedback || ''} ${q.errorAnalysis[0].remediation}`.trim() : q.feedback), options: Array.isArray(q.options) ? q.options.map((opt, oIdx) => ({ text: typeof opt === 'string' ? opt : opt.text || '', isCorrect: q.correctAnswer === oIdx || opt.isCorrect || false })) : [] })), passingScore: 100 }}
                    mode="micro" embedded={true}
                    onComplete={() => {
                      if (!isTransitioning) {
                        setIsTransitioning(true);
                        validateEvidence(`micro-${stepId}`, { type: 'quiz', granuleId: `micro-${stepId}`, score: 100, passed: true, timeSpent: 0 }).catch(e => console.warn('micro-quiz track failed:', e));
                        safeTimeout(() => { setCurrentStepIdx(i => i + 1); setIsTransitioning(false); }, 2500);
                      }
                    }}
                    onFail={handleQuizFail} learnerProfile={learnerProfile}
                  />
                </div>
              )}

              {/* PROGRESSIVE REVEAL */}
              {currentStep?.type === 'progressive-reveal' && (
                <div className="my-8 space-y-4 max-w-4xl mx-auto">
                  <EnrichedModal isOpen={!!activeModal} onClose={() => setActiveModal(null)} data={activeModal} />
                  {currentStep.introduction && <p className="text-xl text-slate-400 italic mb-10 leading-relaxed font-serif">{currentStep.introduction}</p>}
                  {currentStep.cards?.filter((card, i, arr) => cardFilter(card, i, arr)).map((card, cardIdx) => (
                    <motion.div key={card.id || cardIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: cardIdx * 0.15 }} className="rounded-2xl border border-white/5 p-8 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                      {card.conceptualDepth && <span className="inline-block mb-3 px-2 py-0.5 rounded border border-white/5 bg-white/[0.02] text-[8px] font-mono text-white/20 uppercase tracking-widest">Niveau_{card.conceptualDepth}</span>}
                      <div className="flex items-start gap-3 mb-3">
                        {card.teaserVisual && <div className="text-3xl p-2 rounded-xl bg-white/5 border border-white/5 shrink-0">{getTeaserIcon(card.teaserVisual)}</div>}
                        <div className="flex-1">
                          <h3 className="text-[10px] font-black text-cyan-500 uppercase tracking-widest mb-1 font-mono">Extrait_Donnees // {cardIdx + 1}</h3>
                          <p className="text-lg text-white font-medium">{card.teaserTitle || card.title}</p>
                          {card.teaserDurationHint && <span className="inline-block mt-1 text-[9px] font-mono text-white/20 uppercase"><Clock size={10} className="inline mr-1" />{card.teaserDurationHint}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">{card.teaserText || card.content}</p>
                      {card.teaserText && card.content && card.content !== card.teaserText && <div className="mt-4 pt-4 border-t border-white/5"><p className="text-sm text-slate-500 leading-relaxed italic">{card.content}</p></div>}
                      <KeyQuote quote={card.keyQuote} />
                      {card.realityCheck && <ContextualNote type="realityCheck" content={card.realityCheck} />}
                      {card.paradoxe && <ContextualNote type="paradoxe" content={card.paradoxe} />}
                      {card.differentiation && <ContextualNote type="differentiation" content={card.differentiation} />}
                      {card.critiqueBourdieusienne && <ContextualNote type="critiqueBourdieusienne" content={card.critiqueBourdieusienne} />}
                      {card.conditionalSuccess && <ContextualNote type="conditionalSuccess" content={card.conditionalSuccess} />}
                      <PesaTransferBadge actionId={card.pesaActionId} text={card.transferExample} />
                      {card.modal && (
                        <button onClick={() => setActiveModal({ ...card.modal, title: card.modal.title || card.title, conceptualDepth: card.conceptualDepth })} className="mt-6 flex items-center gap-3 px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all group">
                          <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center text-[10px] font-bold border border-cyan-500/40 group-hover:scale-110 transition-transform">+</div>
                          <span className="text-[10px] font-black font-mono uppercase tracking-[0.15em]">Approfondir_Concept</span>
                        </button>
                      )}
                    </motion.div>
                  ))}
                  {currentStep.finalQuestion && (
                    <div className="pt-8 border-t border-white/5">
                      <p className="text-cyan-400 font-bold mb-4 text-lg tracking-tight uppercase">{currentStep.finalQuestion}</p>
                      <VocalOrTextInput stepId={stepId} label="Consigner l'analyse" onSubmit={handleVocalSubmit(`${stepId}-final`)} />
                    </div>
                  )}
                </div>
              )}

              {/* CASE STUDY */}
              {currentStep?.type === 'case-study' && (
                <div className="my-8 max-w-4xl mx-auto">
                  <div className="bg-slate-900/40 border border-white/5 rounded-2xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_8px_#10b981]" />
                      <h3 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Etude de cas // {currentStep.title}</h3>
                    </div>
                    <p className="text-lg text-slate-300 leading-relaxed mb-8 font-serif italic">{currentStep.situation}</p>
                    {currentStep.questions?.map((q, qIdx) => (
                      <div key={q.id || qIdx} className="mb-8 border-b border-white/5 pb-6 last:border-0">
                        <p className="text-sm font-bold text-white mb-3 flex gap-2"><span className="text-cyan-500/60 text-[10px] font-mono">Q{qIdx+1}</span>{q.text}</p>
                        {q.hint && <div className="flex gap-2 items-start text-[11px] text-slate-500 mb-3"><HelpCircle size={12} className="shrink-0 mt-0.5" /><span className="italic">{q.hint}</span></div>}
                        <VocalOrTextInput stepId={`${stepId}-q${qIdx}`} label="Votre reponse..." onSubmit={handleVocalSubmit(`${stepId}-q${qIdx}`)} />
                      </div>
                    ))}
                    {currentStep.correction && (
                      <details className="mt-6 group">
                        <summary className="cursor-pointer text-emerald-400 font-bold text-sm uppercase tracking-wider hover:text-emerald-300 transition-colors">Voir le corrige type</summary>
                        <div className="mt-4 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10 text-slate-400 text-sm leading-relaxed">{currentStep.correction}</div>
                      </details>
                    )}
                  </div>
                </div>
              )}

              {/* KEY INSIGHTS */}
              {currentStep?.type === 'key-insights' && currentStep.bullets && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                  {currentStep.bullets.map((b, i) => (
                    <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10 flex gap-4">
                      <span className="text-cyan-500 font-mono text-xs mt-1">»</span>
                      <p className="text-sm text-slate-300 leading-relaxed">{b}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* VISUAL ANALYSIS */}
              {currentStep?.type === 'visual-analysis' && currentStep.images && (
                <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl my-8">
                  <ImageComparator images={currentStep.images} />
                </div>
              )}

              {/* TRIGGER / EXPERIENTIAL */}
              {['trigger', 'experiential-trigger'].includes(currentStep?.type) && (
                <div className="my-8 p-10 bg-slate-900/40 rounded-[2.5rem] border border-white/5 shadow-inner">
                  {(currentStep.duration || currentStep.required) && (
                    <div className="flex items-center gap-3 text-sm text-slate-400 mb-4">
                      {currentStep.duration && <span className="flex items-center gap-1"><ClockIcon className="w-4 h-4" />{currentStep.duration} sec</span>}
                      {currentStep.required && <span className="text-amber-400 font-medium">* Requis</span>}
                    </div>
                  )}
                  {currentStep.anchorType && <div className="mt-2 text-xs text-slate-500 italic">Ancrage : {currentStep.anchorType === 'autobiographical' ? 'autobiographique' : currentStep.anchorType}</div>}
                  <p className="text-xl text-slate-200 mb-10 leading-relaxed font-medium">{currentStep.text || currentStep.prompt}</p>
                  <VocalOrTextInput stepId={stepId} label={currentStep.placeholder || 'Saisir la reflexion...'} onSubmit={handleVocalSubmit(stepId)} validationMessage={currentStep.validationMessage} />
                  <PesaEchoPanel agent={currentStep.pesaEchoAgent} context={currentStep.pesaEchoContext} />
                </div>
              )}

              {/* PERSONAL SYNTHESIS */}
              {currentStep?.type === 'personal-synthesis' && (
                <div className="my-8 p-10 bg-slate-900/40 rounded-[2.5rem] border border-white/5 shadow-inner">
                  {(() => {
                    const baseText = currentStep.text || currentStep.prompt || '';
                    const prompt = currentStep.harvestEcho ? baseText.replace('{harvestEcho}', resolveHarvestEcho(currentStep.harvestEcho, harvestedData)) : baseText;
                    return <p className="text-xl text-slate-200 mb-8 leading-relaxed font-medium">{prompt}</p>;
                  })()}
                  {currentStep.selfAssessment && (
                    <div className="mb-8">
                      <p className="text-sm text-slate-400 mb-4 font-medium">{currentStep.selfAssessment.prompt}</p>
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        {currentStep.selfAssessment.options.map((opt) => (
                          <button key={opt.level} type="button" onClick={() => { setSelfAssessmentValue(opt.level); updateHarvest(currentStep.selfAssessment.harvestKey, opt.level); }} className={`p-4 rounded-xl border transition-all text-center ${selfAssessmentValue === opt.level ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400' : 'border-white/5 bg-white/[0.02] text-slate-400 hover:border-white/10'}`}>
                            <span className="text-2xl block mb-1">{opt.emoji}</span>
                            <span className="text-xs font-mono tracking-wide">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                      {showSelfGapFeedback && currentStep.evaluationGrid?.mentorLevel && (
                        <div className={`p-3 rounded-lg text-xs font-mono border ${selfGapFeedback.type === 'overestimated' ? 'border-yellow-500/30 bg-yellow-500/5 text-yellow-300' : selfGapFeedback.type === 'underestimated' ? 'border-purple-500/30 bg-purple-500/5 text-purple-300' : 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300'}`}>
                          {selfGapFeedback.message}
                        </div>
                      )}
                    </div>
                  )}
                  <EvaluationGrid grid={currentStep.evaluationGrid} />
                  <VocalOrTextInput stepId={stepId} label={currentStep.placeholder || 'Saisir la reflexion...'} onSubmit={(value) => handleSubmitWithSelfAssessment(stepId, value)} />
                  <HarvestConsequence consequence={currentStep.harvestIntegration?.pesaConsequence} />
                </div>
              )}

              {/* SYNTHESIS */}
              {currentStep?.type === 'synthesis' && (
                <div className="my-8 p-10 bg-slate-900/40 rounded-[2.5rem] border border-white/5 shadow-inner">
                  <p className="text-xl text-slate-200 mb-10 leading-relaxed font-medium">{currentStep.text || currentStep.prompt}</p>
                  <VocalOrTextInput stepId={stepId} label={currentStep.placeholder || 'Saisir la reflexion...'} onSubmit={handleVocalSubmit(stepId)} />
                </div>
              )}

              {/* EMOTIONAL CHECKLIST */}
              {currentStep?.type === 'emotional-checklist' && (
                <div className="my-8 max-w-4xl mx-auto">
                  {(currentStep.text || currentStep.title) && <p className="text-lg text-slate-300 mb-8 pb-4 border-b border-white/5">{currentStep.text || currentStep.title}</p>}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                    {currentStep.options?.map((opt, i) => {
                      const isSelected = selectedEmotions.includes(opt);
                      const agentResonance = currentStep.emotionMapping?.[opt]?.agentResonance;
                      return (
                        <label key={i} className="flex flex-col gap-1.5 p-4 rounded-xl border transition-all cursor-pointer" style={{ background: isSelected ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.02)', borderColor: isSelected ? 'rgba(99,102,241,0.6)' : 'rgba(255,255,255,0.07)', color: isSelected ? '#a5b4fc' : 'rgba(148,163,184,0.8)' }}>
                          <input type="checkbox" className="hidden" checked={isSelected} onChange={(e) => { const next = e.target.checked ? [...selectedEmotions, opt] : selectedEmotions.filter(x => x !== opt); setSelectedEmotions(next); updateHarvest(stepId, { type: 'emotions', selected: next }); }} />
                          <span className="text-sm font-bold uppercase tracking-tighter">{opt}</span>
                          {isSelected && agentResonance && <span className="text-[8px] font-mono text-indigo-400/60 uppercase tracking-widest">{agentResonance}</span>}
                        </label>
                      );
                    })}
                  </div>
                  {currentStep.pedagogicalNote && (
                    <div className="mb-6 p-4 rounded-xl border border-white/5 bg-white/[0.02] flex gap-3">
                      <BookOpen size={12} className="text-white/20 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-slate-500 leading-relaxed italic">{currentStep.pedagogicalNote}</p>
                    </div>
                  )}
                  <VocalOrTextInput stepId={`${stepId}-annotation`} label="Annotation complementaire (optionnel)" onSubmit={handleVocalSubmit(`${stepId}-annotation`)} />
                </div>
              )}

              {/* EMPOWERING CLOSURE */}
              {currentStep?.type === 'empowering-closure' && (
                <div className="my-10 py-12 px-6 bg-cyan-500/5 border border-cyan-500/10 rounded-[3rem] relative">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(34,211,238,0.1),_transparent_70%)] pointer-events-none" />
                  {(() => {
                    const closureText = currentStep.dynamicText ? resolveClosureText(currentStep.dynamicText, harvestedData, learnerProfile) : currentStep.harvestEcho ? resolveHarvestEcho(currentStep.harvestEcho, harvestedData) : currentStep.text;
                    return (
                      <div className="prose prose-invert max-w-2xl mx-auto text-lg text-slate-400 leading-relaxed space-y-4 font-serif italic">
                        {closureText.split('\n').map((line, i) => <p key={i}>{line || <br />}</p>)}
                      </div>
                    );
                  })()}
                  <div className="max-w-2xl mx-auto"><ClosureFooter step={currentStep} /></div>
                </div>
              )}

              {/* THEORY INVITATION */}
              {currentStep?.type === 'theory-invitation' && (
                <div className="my-10 p-1 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-[2rem]">
                  <div className="bg-slate-950/90 backdrop-blur-xl p-10 rounded-[1.9rem] border border-white/5 relative overflow-hidden">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
                    {(() => {
                      const why = currentStep.whyForYou;
                      const invitationText = (() => {
                        if (!why) return currentStep.text;
                        const pt = learnerProfile.type;
                        if (pt?.startsWith('emotionProfile')) return why[`ifEmotionProfile_${learnerProfile.emotionSignal}`] || why.default || currentStep.text;
                        if (pt?.startsWith('notionProfile')) return why[`ifNotionProfile_${learnerProfile.notionProfile}`] || why.default || currentStep.text;
                        return why.default || currentStep.text;
                      })();
                      return <p className="text-xl text-slate-300 mb-12 leading-relaxed max-w-2xl mx-auto relative z-10">{invitationText}</p>;
                    })()}
                    <div className="flex flex-wrap items-center justify-center gap-6 relative z-10">
                      <button onClick={() => { const mediaRef = currentStep.mediaReference; if (mediaRef) { const src = mediaRef.file ? resolveAsset(mediaRef.file) : mediaRef.url; if (src) setEmbeddedMedia({ type: mediaRef.type || 'video', src, title: mediaRef.title }); } }} className="group relative px-8 py-4 bg-cyan-500 text-black font-black text-[10px] uppercase tracking-[0.2em] rounded-full overflow-hidden transition-all hover:scale-105 active:scale-95">
                        <span className="relative z-10 flex items-center gap-2"><Play size={14} fill="currentColor" /> Initialiser_Visualisation</span>
                        <div className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 opacity-20" />
                      </button>
                      <button onClick={() => { const file = currentStep.resourceReference?.file; if (file) setEmbeddedMedia({ type: 'pdf', src: resolveAsset(file), title: currentStep.resourceReference.title }); }} className="group px-8 py-4 border border-white/10 hover:border-cyan-500/50 rounded-full text-slate-400 hover:text-cyan-400 font-bold text-[10px] uppercase tracking-[0.2em] transition-all flex items-center gap-2">
                        <FileText size={14} /> Acceder_Aux_Documents
                      </button>
                      <button onClick={() => setCurrentStepIdx(i => i + 1)} className="text-slate-600 hover:text-white text-[9px] uppercase tracking-widest transition-colors font-mono">[ Ignorer_Analyse ]</button>
                    </div>
                    {currentStep.pesaUnlockCondition && (
                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-10 mx-auto max-w-xl p-4 rounded-xl border border-cyan-500/10 bg-cyan-500/[0.04] relative z-10">
                        <div className="flex gap-3">
                          <Sparkles size={11} className="text-cyan-500/40 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[8px] font-mono text-cyan-500/40 uppercase tracking-widest mb-1">Completer_debloque</p>
                            <p className="text-[11px] text-slate-500 leading-relaxed">{currentStep.pesaUnlockCondition}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    {currentStep.adaptiveLogic?.ifSkipped && <p className="mt-4 text-center text-[9px] text-white/10 font-mono">[ Si ignore  {currentStep.adaptiveLogic.ifSkipped.slice(0, 60)}... ]</p>}
                  </div>
                </div>
              )}

              <AnimatePresence>
                {embeddedMedia && <FloatingVideoPlayer media={embeddedMedia} onClose={clearEmbeddedMedia} />}
              </AnimatePresence>

              {['visual-analysis', 'key-insights'].includes(currentStep?.type) && currentStep.quickCheck && (
                <div className="pt-8 border-t border-white/5">
                  <p className="text-cyan-400 font-bold mb-4 text-lg tracking-tight uppercase">{currentStep.quickCheck}</p>
                  <VocalOrTextInput stepId={`${stepId}-check`} label="Consigner l'analyse" onSubmit={handleVocalSubmit(`${stepId}-check`)} />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="mt-10 flex flex-col-reverse sm:flex-row items-center gap-4 sm:justify-between border-t border-white/5 pt-8 px-4">
            <button onClick={() => setCurrentStepIdx(i => Math.max(0, i - 1))} disabled={currentStepIdx === 0} className="order-2 sm:order-none flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-white disabled:opacity-0 transition-all">
              Etape_Precedente
            </button>
            {currentStepIdx === adaptedSteps.length - 1 ? (
              <button onClick={() => { if (comprehensionQuiz) setPhase('quiz'); else handleCompleteProtocol(); }} className={comprehensionQuiz ? 'px-10 py-4 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-black uppercase tracking-widest text-[10px] transition-all shadow-lg shadow-purple-900/20' : S.btn}>
                {comprehensionQuiz ? 'Lancer_Evaluation' : 'Terminer_Sequence'}
              </button>
            ) : (
              <button onClick={() => setCurrentStepIdx(i => i + 1)} className={`${S.btn} flex items-center gap-3`}>
                Etape_Suivante <span className="opacity-50"></span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // PHASE: COMPREHENSION QUIZ
  if (phase === 'quiz' && comprehensionQuiz) {
    const normalizedQuiz = { title: comprehensionQuiz.title || 'Quiz de comprehension', passingScore: comprehensionQuiz.passingScore || 70, questions: comprehensionQuiz.questions || [] };
    const handleComprehensionComplete = (quizResults) => {
      const moduleId = currentModule?.id || currentModule?._id;
      if (moduleId) validateGranule(moduleId, { score: quizResults.score || 0, passed: quizResults.passed || false, quizId: comprehensionQuiz._id || null, answers: quizResults.answers || [] });
      if (!isTransitioning) handleNavigation('complete', true, { score: 100 });
    };
    const handleComprehensionFail = (quizResults) => {
      const moduleId = currentModule?.id || currentModule?._id;
      if (moduleId) validateGranule(moduleId, { score: quizResults.score || 0, passed: false, quizId: comprehensionQuiz._id || null, answers: quizResults.answers || [] });
      setIsTransitioning(false);
      handleQuizFail();
    };
    return (
      <div className="h-full w-full flex flex-col overflow-hidden">
        <div className="shrink-0 px-8 py-5 border-b border-white/5 bg-purple-500/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse shadow-[0_0_8px_#a855f7]" />
            <div>
              <span className={`${S.label} text-purple-400 block`}>Protocole_Validation</span>
              <h2 className="text-sm font-bold uppercase text-white">{normalizedQuiz.title}</h2>
            </div>
          </div>
          <div className="text-right">
            <span className={`${S.label} text-white/30`}>Score_Requis</span>
            <p className="font-black font-mono text-purple-400">{normalizedQuiz.passingScore}%</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <QuizEngine quizData={normalizedQuiz} mode="comprehension" onComplete={handleComprehensionComplete} onFail={handleComprehensionFail} mediatorOnFail={mediatorOnFail} onClose={closeActiveItem} embedded={true} learnerProfile={learnerProfile} />
        </div>
      </div>
    );
  }

  // PHASE: MEDIATOR
  if (phase === 'mediator') {
    return (
      <div className="max-w-xl mx-auto py-32 text-center px-6">
        <div className="w-20 h-20 mx-auto mb-8 rounded-[2rem] bg-orange-500/10 border border-orange-500/50 flex items-center justify-center text-orange-500">
          <AlertCircle size={40} strokeWidth={1.5} />
        </div>
        <h2 className="text-2xl font-black text-white mb-4 uppercase tracking-tighter">Renforcement necessaire</h2>
        <p className="text-slate-400 text-sm leading-relaxed mb-10 font-medium">Le seuil de validation n'a pas ete atteint.<br />Le <span className="text-cyan-400 font-mono italic">Neural Narrator</span> a active un guide de soutien.</p>
        <button onClick={() => setPhase(alternativePath ? 'alternative' : 'complete')} className={S.btnSecondary}>Continuer_Protocole</button>
      </div>
    );
  }

  // PHASE: ALTERNATIVE PATH
  if (phase === 'alternative' && alternativePath) {
    return (
      <div className="max-w-2xl mx-auto py-32 text-center px-6">
        <div className="inline-block px-4 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 text-[10px] font-mono mb-6 uppercase tracking-widest">Chemin_Alternatif_Detecte</div>
        <h2 className="text-3xl font-bold text-white mb-6 tracking-tight">{alternativePath.title || 'Chemin alternatif'}</h2>
        <p className="text-slate-400 mb-12 leading-relaxed">{alternativePath.description}</p>
        <button onClick={() => setPhase('complete')} className="px-12 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-black uppercase tracking-widest text-[10px] transition-all shadow-lg shadow-emerald-900/20">Valider_et_Terminer</button>
      </div>
    );
  }

  // PHASE: COMPLETE
  return (
    <div className="max-w-3xl mx-auto py-40 text-center">
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', duration: 0.6 }}>
        <CheckCircle size={100} className="mx-auto text-cyan-400 mb-10" />
        <h2 className="text-4xl font-black text-white mb-6">Exploration terminee</h2>
        <p className="text-xl text-slate-400 mb-10">Vos reflexions sont enregistrees et disponibles dans le Labo Activites Pratiques.</p>
        <button onClick={closeActiveItem} className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all">Fermer le granule</button>
      </motion.div>
    </div>
  );
};

export default ScenarioPlayer;